import type { ApiClient } from '../apiClient';

/**
 * Guardians — the people who can act when you cannot.
 *
 * The vault key is split between them (Shamir). No single guardian can open
 * anything alone; a threshold of them together can. The client never sees share
 * material — these calls carry metadata only.
 */

export type GuardianRole = 'primary' | 'backup' | 'co_signer';
export type GuardianStatus = 'invited' | 'active' | 'inactive' | 'replaced' | 'declined';

export interface Guardian {
  id: string;
  invited_name: string;
  invited_email: string;
  role: GuardianRole;
  status: GuardianStatus;
  accepted_at: string | null;
  invitation_sent_at: string | null;
  invitation_expires_at: string | null;
  /** They have a key registered — but that alone does not mean they can help. */
  has_registered_key: boolean;
  /** They actually hold a piece of the key. This is the one that matters. */
  holds_share: boolean;
}

/**
 * The state of the Perpetuity Covenant for this vault.
 *
 * The escrow is NOT how a vault is released — a release is authorized by the
 * guardians and executed by the server. The escrow exists for one scenario:
 * Legacy Architect no longer exists. Two of three guardians can then rebuild the
 * real vault key from the pieces sealed to their own keys, and recover the
 * messages without us.
 *
 * `epoch` is what makes that safe. Every commit is a fresh split, and pieces
 * from different splits can never be combined — so a vault holding a mix of old
 * and new pieces would look perfectly healthy and be permanently unopenable.
 * Stamping each piece with its epoch makes that state unrepresentable rather
 * than merely unlikely.
 */
export interface GuardianEscrowState {
  /** Current escrow epoch. 0 = never committed — nothing is protected yet. */
  epoch: number;
  /** When the CURRENT epoch was sealed. Null after any change to the guardian set. */
  committed_at: string | null;
  /** Guardians holding a piece of the CURRENT epoch. */
  share_holders: number;
  threshold: number;
  /** The guardian set has changed since the last seal — the owner must re-seal. */
  recommit_required: boolean;
  /** Enough guardians have registered a key that a seal is possible right now. */
  can_commit: boolean;
}

export interface GuardianList {
  guardians: Guardian[];
  /** How many guardians actually hold a piece of the key right now. */
  active_share_holders: number;
  /** How many are needed to open the vault together. 2, today. */
  shamir_threshold: number;
  /**
   * True when removing any share-holder would drop the vault below the
   * threshold — i.e. make it permanently unrecoverable. The server refuses
   * such a removal (409); the UI should not offer it.
   */
  at_threshold_floor: boolean;
  escrow: GuardianEscrowState;
}

export interface EscrowCommitResult {
  vault_id: string;
  epoch: number;
  active_share_holders: number;
  threshold: number;
  committed_at: string;
  slots: { slot: 1 | 2 | 3; guardian_id: string; guardian_name: string; committed: boolean }[];
}

export interface CreateGuardianInput {
  invited_email: string;
  invited_name: string;
  role: GuardianRole;
}

export function listGuardians(api: ApiClient, vaultId: string): Promise<GuardianList> {
  return api.get<GuardianList>(`/api/vaults/${vaultId}/guardians`);
}

export function createGuardian(
  api: ApiClient,
  vaultId: string,
  input: CreateGuardianInput,
): Promise<{ guardian: Guardian }> {
  return api.post<{ guardian: Guardian }>(`/api/vaults/${vaultId}/guardians`, input);
}

export function inviteGuardian(api: ApiClient, vaultId: string, guardianId: string): Promise<unknown> {
  return api.post(`/api/vaults/${vaultId}/guardians/${guardianId}/invite`, {});
}

export function removeGuardian(api: ApiClient, vaultId: string, guardianId: string): Promise<unknown> {
  return api.del(`/api/vaults/${vaultId}/guardians/${guardianId}`);
}

/**
 * Name a successor for a guardian WITHOUT removing them or their piece of the
 * key. Non-destructive by design: the outgoing guardian keeps their share until
 * the successor accepts and holds one of their own — which is why this, not
 * removal, is the only safe way to change a guardian who holds a share (removing
 * a share-holder at the threshold floor would make the vault unopenable).
 */
export interface ReplaceGuardianResult {
  successor: { id: string; invited_name: string; invited_email: string; status: 'invited' };
  next_step: string;
}
export function replaceGuardian(
  api: ApiClient,
  vaultId: string,
  guardianId: string,
  input: { invited_name: string; invited_email: string },
): Promise<ReplaceGuardianResult> {
  return api.post<ReplaceGuardianResult>(
    `/api/vaults/${vaultId}/guardians/${guardianId}/replace`,
    input,
  );
}

/**
 * Seal the vault key to the guardians' keys — the Perpetuity Covenant, committed.
 *
 * Carries no key material in either direction. The SERVER holds the vault key
 * (it must, to deliver anything), splits it, and seals one piece to each
 * guardian's own public key. It cannot open those pieces afterwards, and neither
 * can we: the private halves were minted in the guardians' own browsers.
 *
 * Safe to run again at any time. Each run is a fresh split with a new epoch,
 * which is precisely what makes a removed guardian's old piece worthless.
 */
export function commitEscrow(api: ApiClient, vaultId: string): Promise<EscrowCommitResult> {
  return api.post<EscrowCommitResult>(`/api/vaults/${vaultId}/guardians/escrow/commit`, {});
}

/** The check-in: the client telling the system they are still here. */
export function checkIn(api: ApiClient): Promise<{ next_checkin_due_at: string | null }> {
  return api.post<{ next_checkin_due_at: string | null }>('/api/vaults/me/checkin', {});
}

export const ROLE_COPY: Record<GuardianRole, { label: string; blurb: string }> = {
  primary: {
    label: 'Primary',
    blurb: 'The first person we turn to. Usually the one who would know before anyone else.',
  },
  backup: {
    label: 'Backup',
    blurb: 'Steps in if your primary cannot be reached. Someone in a different part of your life.',
  },
  co_signer: {
    label: 'Co-signer',
    blurb: 'A second pair of hands. Nothing is released on one person’s word alone.',
  },
};
